import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, Plus, MoonIcon, Sun, Crown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/apiClient";

// Header channel switcher — YouTube-style dropdown that lists every channel
// the owner has, sorted alphabetically. No avatars per ehouser product call
// on 2026-05-12 (the channel HANDLE itself is the personalization).
//
// Anonymity rule we enforce client-side: each channel is its own brand. We
// NEVER label which is the master or hint at ownership in any visible UI —
// the "is_master" flag is used only to suppress the delete button on the
// master channel.
const ChannelSwitcher = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [config, setConfig] = useState({ creation_cost: 100, quarterly_maint_cost: 150 });

  const reload = async () => {
    if (!user) return;
    try {
      const { data } = await api.get(`/me/channels`);
      setChannels(data.items || []);
      setActiveId(data.active_channel_id);
      setConfig({
        creation_cost: data.creation_cost,
        quarterly_maint_cost: data.quarterly_maint_cost,
      });
    } catch (_) { /* silent — switcher is non-critical UX */ }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [user?.id]);

  const switchTo = async (ch) => {
    if (ch.id === activeId) {
      // Already active — the user wants to VIEW this channel's public
      // profile (most common scenario for brand-new users with only one
      // channel, where every click on the menu item is a click on their
      // master). Previously this just closed the menu silently, which
      // looked like a "click does nothing" bug. Now we navigate.
      setOpen(false);
      navigate(`/${ch.username}`);
      return;
    }
    if (ch.is_paused) {
      toast.error("Resume that channel first — it's currently going dark.");
      return;
    }
    try {
      await api.post(`/me/channels/${ch.id}/activate`, {});
      setActiveId(ch.id);
      setOpen(false);
      toast.success(`Switched to /${ch.username}`);
      // Force a re-fetch of /me so any next dashboard call uses fresh state.
      try { await refreshUser?.(); } catch (_) {}
      // Soft-refresh the page to repaint anywhere that displays the
      // "/your-handle" link in the header.
      navigate(`/${ch.username}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't switch channel.");
    }
  };

  const createNew = async () => {
    if ((user?.subscription_tier || "free").toLowerCase() !== "producer") {
      toast.error("Multi-channel broadcasting is a Producer-tier perk.");
      navigate("/pricing");
      return;
    }
    const handle = prompt(
      `New channel handle (lowercase letters, numbers, dot/dash/underscore, 2-32 chars).\n` +
      `Cost: ${config.creation_cost} credits up front + ${config.quarterly_maint_cost} cr / quarter maintenance.\n\n` +
      `Pick something distinct — viewers will NEVER see it's linked to your other channels.`
    );
    if (!handle) return;
    setCreating(true);
    try {
      await api.post(`/channels`,
        { username: handle.trim().toLowerCase() });
      toast.success(`Channel /${handle} created. Switch to it from the menu.`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create channel.");
    }
    setCreating(false);
  };

  if (!user) return null;

  // Pre-launch (during the 60s right after first login while /me/channels
  // is still being fetched), fall back to just showing the username link.
  if (channels.length === 0) {
    return (
      <Link to={`/${user.username}`} className="text-emerald-400 hover:text-emerald-300 font-medium" data-testid="nav-profile">
        /{user.username}
      </Link>
    );
  }

  const active = channels.find((c) => c.id === activeId) || channels[0];

  // Single-channel users (every brand-new account, plus everyone who
  // never upgraded past the free tier) get a direct profile link.
  // Opening a one-item dropdown was pure friction — and worse, clicking
  // the only item in that dropdown used to silently close it instead of
  // navigating, which read as a hard bug. The dropdown UI is reserved
  // for Producers who actually have multiple channels to flip between.
  if (channels.length === 1) {
    return (
      <Link
        to={`/${active.username}`}
        className="text-emerald-400 hover:text-emerald-300 font-medium"
        data-testid="nav-profile"
      >
        /{active.username}
      </Link>
    );
  }

  return (
    <div className="relative" data-testid="channel-switcher">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 font-medium"
        data-testid="channel-switcher-trigger"
      >
        /{active.username}
        {channels.length > 1 && <ChevronDown className="h-4 w-4 -ml-0.5" />}
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            className="absolute right-0 mt-2 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 py-1"
            data-testid="channel-switcher-menu"
          >
            <div className="px-3 py-2 text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-800">
              Your channels
            </div>
            {channels.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => switchTo(c)}
                className={`w-full text-left px-3 py-2 hover:bg-gray-800 transition-colors flex items-center justify-between ${
                  c.id === activeId ? "bg-emerald-500/10 text-emerald-300" : "text-gray-200"
                }`}
                data-testid={`channel-switch-${c.username}`}
              >
                <span className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="truncate">/{c.username}</span>
                  {c.is_paused && (
                    <MoonIcon className="h-3 w-3 text-amber-400 shrink-0" title="Going dark" />
                  )}
                </span>
                {c.id === activeId && <span className="text-[10px] text-emerald-400 ml-2">active</span>}
              </button>
            ))}
            <div className="border-t border-gray-800 mt-1 pt-1">
              <button
                type="button"
                onClick={createNew}
                disabled={creating}
                className="w-full text-left px-3 py-2 hover:bg-gray-800 text-emerald-400 disabled:opacity-50 flex items-center gap-2"
                data-testid="channel-switch-create-new"
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="text-sm">
                  New channel
                  {(user.subscription_tier || "free").toLowerCase() !== "producer" && (
                    <span className="text-[10px] text-amber-400 ml-1.5">
                      <Crown className="inline h-3 w-3 -mt-0.5" /> Producer
                    </span>
                  )}
                </span>
              </button>
              <Link
                to="/dashboard"
                onClick={() => setOpen(false)}
                className="block px-3 py-2 hover:bg-gray-800 text-gray-300 text-xs"
                data-testid="channel-switch-manage"
              >
                Manage channels →
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChannelSwitcher;
